import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { SearchField } from '@/components/ds2'
import { EMAIL_TEMPLATE_VARIABLES } from '../constants/emailTemplateVariables'

interface TemplateVariablePickerProps {
    values: Readonly<Record<string, string>>
    onInsert: (key: string) => void
    /** Replaces the default line under the title, e.g. to say where a variable will go. */
    hint?: string
}

export const TemplateVariablePicker = ({ values, onInsert, hint }: TemplateVariablePickerProps) => {
    const [query, setQuery] = useState('')
    const filtered = useMemo(() => {
        const normalized = query.trim().toLowerCase()
        if (!normalized) return EMAIL_TEMPLATE_VARIABLES
        return EMAIL_TEMPLATE_VARIABLES.filter(({ key, label, category }) =>
            [key, label, category, values[key] ?? ''].some((value) => value.toLowerCase().includes(normalized))
        )
    }, [query, values])
    const categories = [...new Set(filtered.map(({ category }) => category))]

    return (
        <div className="flex flex-col gap-2 rounded-2xl bg-surface-secondary p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <p className="text-sm font-medium text-foreground">Insert variable</p>
                    <p className="text-xs text-foreground-muted">{hint ?? 'The current load value is shown; the saved template keeps the variable.'}</p>
                </div>
                <SearchField value={query} onChange={setQuery} placeholder="Search variables…" showLabel={false} showDescription={false} variant="secondary" />
            </div>
            <div className="max-h-52 overflow-y-auto pr-1">
                {categories.map((category) => (
                    <section key={category} className="mb-3 last:mb-0">
                        <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">{category}</h4>
                        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                            {filtered
                                .filter((variable) => variable.category === category)
                                .map(({ key, label }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => onInsert(key)}
                                        className="flex min-w-0 items-center gap-2 rounded-xl bg-surface px-2.5 py-2 text-left shadow-field transition-colors duration-150 hover:bg-default"
                                    >
                                        <Plus size={14} className="shrink-0 text-accent" />
                                        <span className="min-w-0">
                                            <span className="block truncate text-xs font-medium text-foreground">{label}</span>
                                            <span className="block truncate text-[11px] text-foreground-muted">{values[key] || '—'}</span>
                                        </span>
                                    </button>
                                ))}
                        </div>
                    </section>
                ))}
                {filtered.length === 0 && <p className="py-4 text-center text-sm text-foreground-muted">No matching variables</p>}
            </div>
        </div>
    )
}
